import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BellRinging, Envelope, DeviceMobile, Check } from "@phosphor-icons/react";
import { pushSupported, currentSubscription, subscribePush, unsubscribePush } from "@/lib/push";

const LABELS = {
  below_par: "Below PAR",
  out_of_stock: "Out of Stock",
  count_awaiting_approval: "Count awaiting approval",
  sync_failure: "Offline sync failure",
  expiring_soon: "Expiring soon (coming with batch tracking)",
};

const CADENCES = [
  { value: "immediate", label: "Immediate" },
  { value: "hourly", label: "Hourly digest" },
  { value: "daily", label: "Daily digest" },
  { value: "disabled", label: "Disabled" },
];

const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "push", label: "Push" },
  { value: "both", label: "Both" },
  { value: "none", label: "None" },
];

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState(null);
  const [pushSub, setPushSub] = useState(null);
  const [supported, setSupported] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        api.get("/notifications/preferences"),
        api.get("/notifications/status"),
      ]);
      setPrefs(pRes.data.preferences);
      setRole(pRes.data.role);
      setStatus(sRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    (async () => {
      const ok = await pushSupported();
      setSupported(ok);
      if (ok) setPushSub(await currentSubscription());
    })();
  }, [load]);

  const updatePref = async (alertType, patch) => {
    setBusy(true);
    try {
      const merged = { ...prefs[alertType], ...patch };
      await api.patch("/notifications/preferences", {
        alert_type: alertType,
        channel: merged.channel,
        cadence: merged.cadence,
      });
      setPrefs({ ...prefs, [alertType]: { ...merged, overridden: true } });
      toast.success("Preference saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const resetPref = async (alertType) => {
    setBusy(true);
    try {
      await api.delete(`/notifications/preferences/${alertType}`);
      await load();
      toast.success("Reset to role default");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const togglePush = async () => {
    setBusy(true);
    try {
      if (pushSub) {
        await unsubscribePush();
        setPushSub(null);
        toast.success("Push disabled on this device");
      } else {
        await subscribePush();
        setPushSub(await currentSubscription());
        toast.success("Push enabled on this device");
      }
    } catch (e) {
      toast.error(e.message || "Failed to toggle push");
    } finally {
      setBusy(false);
    }
  };

  if (!prefs || !status) {
    return <div className="p-6 text-sm text-muted-foreground font-mono">Loading preferences…</div>;
  }

  const alertKeys = Object.keys(LABELS);

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="mb-4">
        <div className="label-tech mb-1">You</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Notification Preferences</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Signed in as <span className="font-mono">{role}</span>. Overrides here take priority over the role defaults set by administrators.
        </p>
      </div>

      {/* Channel status */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <div className="card-flat border-border p-4" data-testid="notif-email-status">
          <div className="flex items-center gap-2 mb-1">
            <Envelope size={16} weight="bold" className="text-primary" />
            <div className="label-tech">Email</div>
          </div>
          <div className="font-display font-black text-lg">
            {status.email_mode === "live" ? "Live" : "Dry-run (logged only)"}
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-1">
            Sender: {status.email_sender}
          </div>
        </div>
        <div className="card-flat border-border p-4" data-testid="notif-push-status">
          <div className="flex items-center gap-2 mb-1">
            <DeviceMobile size={16} weight="bold" className="text-primary" />
            <div className="label-tech">Web Push</div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="font-display font-black text-lg">
              {status.web_push_enabled ? (pushSub ? "Subscribed on this device" : "Enabled") : "Disabled"}
            </div>
            {status.web_push_enabled && supported && (
              <button
                data-testid="toggle-push-btn"
                onClick={togglePush}
                disabled={busy}
                className="btn-hard h-9 px-3 text-[11px] font-bold uppercase tracking-widest rounded-sm border border-border hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
              >
                {pushSub ? "Disable" : "Enable"}
              </button>
            )}
          </div>
          {!supported && <div className="text-xs text-muted-foreground mt-2">This browser does not support Web Push. Try Chrome, Edge, or Firefox.</div>}
        </div>
      </div>

      {/* Preferences table */}
      <div className="card-flat border-border overflow-hidden" data-testid="notif-preferences-table">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-2 px-4 py-3 border-b border-border bg-muted/50 label-tech">
          <div>Alert</div>
          <div>Channel</div>
          <div>Cadence</div>
          <div className="text-right">Reset</div>
        </div>
        {alertKeys.map((k) => {
          const p = prefs[k];
          const disabled = k === "expiring_soon";
          return (
            <div key={k} className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-2 px-4 py-3 border-b border-border last:border-b-0 items-center">
              <div>
                <div className="font-bold text-sm">{LABELS[k]}</div>
                <div className="text-[10px] font-mono text-muted-foreground">
                  {p.overridden ? "custom override" : "role default"}
                </div>
              </div>
              <select
                data-testid={`pref-${k}-channel`}
                value={p.channel}
                disabled={disabled}
                onChange={(e) => updatePref(k, { channel: e.target.value })}
                className="h-9 px-2 border border-border bg-background rounded-sm text-sm disabled:opacity-40"
              >
                {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select
                data-testid={`pref-${k}-cadence`}
                value={p.cadence}
                disabled={disabled}
                onChange={(e) => updatePref(k, { cadence: e.target.value })}
                className="h-9 px-2 border border-border bg-background rounded-sm text-sm disabled:opacity-40"
              >
                {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button
                onClick={() => resetPref(k)}
                disabled={!p.overridden || disabled}
                className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground hover:text-primary disabled:opacity-30 px-2"
              >
                Reset
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex gap-3">
        <Link to="/notifications/history" className="btn-hard h-11 px-5 flex items-center gap-2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600">
          <BellRinging size={14} weight="bold" /> View Alert History
        </Link>
        {(role === "admin" || role === "system_owner") && (
          <Link to="/admin/notifications" className="btn-hard h-11 px-5 flex items-center gap-2 border border-border text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-muted">
            <Check size={14} weight="bold" /> Manage role defaults
          </Link>
        )}
      </div>
    </div>
  );
}
